import { Heart, SlidersHorizontal } from "lucide-react";
import { useTranslation } from "react-i18next";

import { FacetGroup, type FacetOption } from "@/components/directory/FacetGroup";
import { cn } from "@/lib/utils/cn";

import type {
  DirectoryFilters as Filters,
  OrganizationTypeServed,
  SectorRef,
  ServiceRegion,
  ServiceType,
} from "../../types";

/**
 * Facet order for a choice catalogue the Django rail reads straight off the
 * model's `CHOICES`. Declared as a `Record` over the generated enum rather than
 * a plain array on purpose: the enum is regenerated from the DRF schema, so a
 * code added on the Django side breaks this file's typecheck until it is listed
 * here — instead of leaving the SPA rail quietly one facet short.
 */
function facetOrder<T extends string>(order: Record<T, null>): T[] {
  return Object.keys(order) as T[];
}

const ORG_TYPES = facetOrder<OrganizationTypeServed>({
  self_employed: null,
  private: null,
  cooperative: null,
  not_for_profit: null,
  economical_development: null,
  municipality: null,
  other: null,
});

// Declaration order of ExpertProfile.ServiceType — the order of the CQDD intake
// form, which the Django rail also renders.
const SERVICE_TYPES = facetOrder<ServiceType>({
  analysis: null,
  coaching: null,
  strategic_planning: null,
  business_startup: null,
  infrastructure: null,
  technology_adoption: null,
  commercialization: null,
  market_development: null,
  technology_demo: null,
  certification: null,
  audit: null,
  investment_support: null,
  research_development: null,
  training: null,
  turnaround: null,
  hiring_support: null,
  other: null,
});

/**
 * Directory filter rail — a search box over the two identities a card shows,
 * the five étape-4 facets (head-office region, services offered, regions
 * served, sectors, organization types), plus a client-side "favorites only"
 * toggle. The facets narrow server-side; search and favourites-only stay in
 * the client, as on the tool rail. Emits a new filter object on every
 * change; the parent owns the state and re-queries.
 */
export function DirectoryFilters({
  regions,
  sectors,
  filters,
  onChange,
  search,
  onSearch,
  favoriteCount,
  favoritesOnly,
  onFavoritesOnlyChange,
  showFavorites = true,
}: {
  regions: ServiceRegion[];
  sectors: SectorRef[];
  filters: Filters;
  onChange: (filters: Filters) => void;
  /** Matched client-side off the card payload, as the tool rail does it. */
  search: string;
  onSearch: (value: string) => void;
  favoriteCount: number;
  favoritesOnly: boolean;
  onFavoritesOnlyChange: (value: boolean) => void;
  /** False when the caller renders the favourites toggle itself (panel chrome). */
  showFavorites?: boolean;
}) {
  const { t } = useTranslation();

  const hasActive =
    Object.values(filters).some((v) => (v?.length ?? 0) > 0) ||
    favoritesOnly ||
    search !== "";

  const toggle = (key: keyof Filters) => (value: string, checked: boolean) => {
    const current = filters[key] ?? [];
    const next = checked
      ? [...current, value]
      : current.filter((v) => v !== value);
    onChange({ ...filters, [key]: next.length ? next : undefined });
  };

  const regionOptions: FacetOption[] = regions.map((r) => ({ value: r.id, label: r.name }));

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
      <div className="flex items-center justify-between border-b border-border/70 px-4 py-3">
        <span className="inline-flex items-center gap-2 text-sm font-bold">
          <SlidersHorizontal className="size-4 text-muted-foreground" aria-hidden />
          {t("expert.filters.heading")}
        </span>
        {hasActive && (
          <button
            type="button"
            onClick={() => {
              onChange({});
              onSearch("");
              onFavoritesOnlyChange(false);
            }}
            className="text-xs font-semibold text-primary hover:underline"
          >
            {t("expert.filters.clear")}
          </button>
        )}
      </div>

      <div className="border-b border-border/70 px-4 py-3">
        <input
          type="search"
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          placeholder={t("expert.filters.search")}
          aria-label={t("expert.filters.searchLabel")}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
        />
      </div>

      {showFavorites && favoriteCount > 0 && (
        <button
          type="button"
          aria-pressed={favoritesOnly}
          onClick={() => onFavoritesOnlyChange(!favoritesOnly)}
          className={cn(
            "flex min-h-touch w-full items-center gap-2.5 border-b border-border/70 px-4 py-3 text-sm font-semibold",
            favoritesOnly
              ? "bg-primary/10 text-primary"
              : "text-foreground hover:bg-accent",
          )}
        >
          <Heart
            className={cn("size-4", favoritesOnly && "fill-primary")}
            aria-hidden
          />
          {t("expert.filters.favoritesOnly")}
          <span
            className={cn(
              "ml-auto rounded-full px-2 py-0.5 text-xs font-bold tabular-nums",
              favoritesOnly
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground",
            )}
          >
            {favoriteCount}
          </span>
        </button>
      )}

      <FacetGroup
        label={t("expert.filters.originRegions")}
        options={regionOptions}
        selected={filters.origin_regions ?? []}
        onToggle={toggle("origin_regions")}
        defaultOpen
      />
      <FacetGroup
        label={t("expert.filters.serviceTypes")}
        options={SERVICE_TYPES.map((v) => ({
          value: v,
          label: t(`expert.serviceTypes.${v}`),
        }))}
        selected={filters.service_types ?? []}
        onToggle={toggle("service_types")}
      />
      <FacetGroup
        label={t("expert.filters.serviceRegions")}
        options={regionOptions}
        selected={filters.service_regions ?? []}
        onToggle={toggle("service_regions")}
      />
      {/* The enterprise's own sectors only; none declared means no facet, as
          on the Django rail. */}
      {sectors.length > 0 && (
        <FacetGroup
          label={t("expert.filters.sectors")}
          options={sectors.map((s) => ({ value: s.id, label: s.name }))}
          selected={filters.sectors_served ?? []}
          onToggle={toggle("sectors_served")}
        />
      )}
      <FacetGroup
        label={t("expert.filters.orgTypes")}
        options={ORG_TYPES.map((v) => ({ value: v, label: t(`expert.orgTypes.${v}`) }))}
        selected={filters.organization_types_served ?? []}
        onToggle={toggle("organization_types_served")}
      />
    </div>
  );
}
